import { invokeV4, tableRows } from "../../shared/api/v4Remote";
import { getSupabaseClient } from "../../shared/api/supabase";
import type {
  EnergyClaim,
  EnergyAppeal,
  EnergyLedger,
  EnergyRule,
  IncentivesState,
  EmployeeHonor,
  EnergyClearing,
} from "./incentives.types";
type Row = Record<string, unknown>;
const text = (value: unknown) => String(value ?? "");
export const remoteIncentivesRepository = {
  async getState(): Promise<IncentivesState> {
    const [rules, claims, appeals, ledger, honors, clearings] = await Promise.all([
      tableRows<Row>("energy_rules"),
      tableRows<Row>("energy_claims"),
      tableRows<Row>("energy_appeals"),
      tableRows<Row>("energy_ledger"),
      tableRows<Row>("employee_honors"),
      tableRows<Row>("energy_clearings"),
    ]);
    const evidencePaths = claims.map((row) => text(row.evidence_path)).filter(Boolean);
    const signedEvidence = evidencePaths.length
      ? await getSupabaseClient().storage.from("energy-evidence").createSignedUrls(evidencePaths, 900)
      : { data: [], error: null };
    if (signedEvidence.error) throw new Error(signedEvidence.error.message);
    const evidenceUrls = new Map(
      (signedEvidence.data ?? []).map((item, index) => [evidencePaths[index], item.signedUrl]),
    );
    return {
      rules: rules.map(
        (r) =>
          ({
            id: text(r.id),
            code: text(r.code),
            name: text(r.name),
            description: text(r.description),
            points: Number(r.points),
            scope: r.scope,
            storeId: r.store_id ?? null,
            active: Boolean(r.active),
            createdBy: text(r.created_by),
            createdAt: text(r.created_at),
          }) as EnergyRule,
      ),
      claims: claims.map(
        (r) =>
          ({
            id: text(r.id),
            ruleId: text(r.rule_id),
            employeeId: text(r.employee_id),
            storeId: text(r.store_id),
            evidence: text(r.evidence),
            evidencePath: r.evidence_path ? text(r.evidence_path) : null,
            evidenceFileName: text(r.evidence_file_name),
            evidenceMimeType: text(r.evidence_mime_type),
            evidenceFileSize: r.evidence_file_size == null ? null : Number(r.evidence_file_size),
            evidencePreviewUrl: evidenceUrls.get(text(r.evidence_path)) ?? "",
            occurredOn: text(r.occurred_on),
            status: r.status,
            reviewerId: r.reviewer_id ?? null,
            reviewReason: text(r.review_reason),
            createdAt: text(r.created_at),
            reviewedAt: r.reviewed_at ?? null,
          }) as EnergyClaim,
      ),
      appeals: appeals.map(
        (r) =>
          ({
            id: text(r.id),
            claimId: text(r.claim_id),
            employeeId: text(r.employee_id),
            storeId: text(r.store_id),
            reason: text(r.reason),
            status: r.status,
            reviewerId: r.reviewer_id ?? null,
            reviewReason: text(r.review_reason),
            createdAt: text(r.created_at),
            reviewedAt: r.reviewed_at ?? null,
          }) as EnergyAppeal,
      ),
      ledger: ledger.map(
        (r) =>
          ({
            id: text(r.id),
            employeeId: text(r.employee_id),
            storeId: text(r.store_id),
            changePoints: Number(r.change_points),
            balanceAfter: Number(r.balance_after),
            sourceType: r.source_type,
            sourceId: text(r.source_id),
            description: text(r.description),
            occurredAt: text(r.occurred_at),
          }) as EnergyLedger,
      ),
      honors: honors.map((r) => ({ id: text(r.id), employeeId: text(r.employee_id), storeId: text(r.store_id), title: text(r.title), description: text(r.description), badge: text(r.badge), points: Number(r.points), awardedBy: text(r.awarded_by), awardedAt: text(r.awarded_at) }) as EmployeeHonor),
      clearings: clearings.map((r) => ({ id: text(r.id), year: Number(r.year), carryoverLimit: Number(r.carryover_limit), employeeCount: Number(r.employee_count), clearedPoints: Number(r.cleared_points), executedBy: text(r.executed_by), executedAt: text(r.executed_at) }) as EnergyClearing),
    };
  },
  createRule(_actor: unknown, input: Record<string, unknown>) {
    return invokeV4<{ id: string }>("v4-incentives", "createRule", input);
  },
  async submitClaim(_actor: unknown, input: { ruleId: string; evidence: string; occurredOn: string; file?: File | null }) {
    let evidenceFile: Record<string, unknown> = {};
    if (input.file) {
      const prepared = await invokeV4<{ path: string; token: string }>("v4-incentives", "createClaimEvidenceUpload", {
        fileName: input.file.name,
        mimeType: input.file.type,
        fileSize: input.file.size,
      });
      const { error } = await getSupabaseClient().storage
        .from("energy-evidence")
        .uploadToSignedUrl(prepared.path, prepared.token, input.file, { contentType: input.file.type });
      if (error) throw new Error(error.message);
      evidenceFile = {
        evidencePath: prepared.path,
        evidenceFileName: input.file.name,
        evidenceMimeType: input.file.type,
        evidenceFileSize: input.file.size,
      };
    }
    return invokeV4<{ id: string }>("v4-incentives", "submitClaim", {
      ruleId: input.ruleId,
      evidence: input.evidence,
      occurredOn: input.occurredOn,
      ...evidenceFile,
    });
  },
  reviewClaim(_actor: unknown, input: Record<string, unknown>) {
    return invokeV4<{ id: string }>("v4-incentives", "reviewClaim", input);
  },
  submitAppeal(_actor: unknown, input: Record<string, unknown>) {
    return invokeV4<{ id: string }>("v4-incentives", "submitAppeal", input);
  },
  reviewAppeal(_actor: unknown, input: Record<string, unknown>) {
    return invokeV4<{ id: string }>("v4-incentives", "reviewAppeal", input);
  },
  adjust(_actor: unknown, input: Record<string, unknown>) {
    return invokeV4<{ id: string }>("v4-incentives", "adjust", input);
  },
  awardHonor(_actor: unknown, input: Record<string, unknown>) { return invokeV4<{ id: string }>("v4-incentives", "awardHonor", input); },
  runAnnualClearing(_actor: unknown, input: Record<string, unknown>) { return invokeV4<{ id: string }>("v4-incentives", "runAnnualClearing", input); },
};
